import type { ClientSocket } from './game-store';
import { Device, type types as MsTypes } from 'mediasoup-client';

export interface VoiceManagerOptions {
  socket: ClientSocket;
}

export interface PeerChangeInfo {
  playerId: string;
  enabled: boolean;
}

export interface VoiceError {
  code: string;
  message: string;
}

export type VoiceErrorHandler = (err: VoiceError) => void;
export type PeerChangeHandler = (info: PeerChangeInfo) => void;

interface TransportOpts {
  ok: true;
  transportId: string;
  iceParameters: MsTypes.IceParameters;
  iceCandidates: MsTypes.IceCandidate[];
  dtlsParameters: MsTypes.DtlsParameters;
}

interface ConsumerOpts {
  ok: true;
  consumerId: string;
  producerId: string;
  kind: 'audio' | 'video';
  rtpParameters: MsTypes.RtpParameters;
}

interface RtpCapsAck {
  ok: true;
  rtpCapabilities: MsTypes.RtpCapabilities;
}

interface ErrAck {
  ok: false;
  error: string;
}

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
];

export class VoiceManager {
  private readonly socket: ClientSocket;
  private device: Device | null = null;
  private sendTransport: MsTypes.Transport | null = null;
  private recvTransport: MsTypes.Transport | null = null;
  private producer: MsTypes.Producer | null = null;
  private consumers = new Map<string, MsTypes.Consumer>();
  private remoteAudios = new Map<string, HTMLAudioElement>();
  private enabled = false;
  private muted = true;
  private localStream: MediaStream | null = null;
  private peerHandlers = new Set<PeerChangeHandler>();
  private errorHandlers = new Set<VoiceErrorHandler>();
  private stateHandlers = new Set<() => void>();
  private socketHandlersBound = false;
  private startingPromise: Promise<void> | null = null;
  private wasEnabled = false;
  private autoStartOnMount = false;

  constructor(opts: VoiceManagerOptions) {
    this.socket = opts.socket;
    this.bindSocketHandlers();
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  wasEnabledBeforeDisconnect(): boolean {
    return this.wasEnabled;
  }

  /**
   * Marca que el siguiente render de un <jgz-voice-controls> debe activar el
   * micrófono automáticamente. Pensado para que el dueño del ciclo de vida
   * (app-root) lo invoque al crear el manager; el componente lo consume una
   * sola vez cuando monta. Un cambio de vista posterior (lobby → play) no
   * vuelve a disparar el auto-start porque la flag ya fue consumida.
   */
  enableOnMount(): void {
    this.autoStartOnMount = true;
  }

  /**
   * Consumir la señal de auto-start. Devuelve true solo si el manager está
   * marcado y todavía no está habilitado (evita un segundo start cuando la
   * reconexión por socket ya lo habilitó). Tras consumir, resetea la flag.
   */
  consumeAutoStartOnMount(): boolean {
    if (this.autoStartOnMount && !this.enabled) {
      this.autoStartOnMount = false;
      return true;
    }
    this.autoStartOnMount = false;
    return false;
  }

  isMicActive(): boolean {
    return this.enabled && !this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.producer) {
      if (muted) {
        this.producer.pause();
      } else {
        this.producer.resume();
      }
    }
    for (const audio of this.remoteAudios.values()) {
      audio.muted = muted;
    }
    this.notifyStateChange();
  }

  onPeerChange(handler: PeerChangeHandler): () => void {
    this.peerHandlers.add(handler);
    return () => this.peerHandlers.delete(handler);
  }

  onError(handler: VoiceErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  onStateChange(handler: () => void): () => void {
    this.stateHandlers.add(handler);
    return () => this.stateHandlers.delete(handler);
  }

  async start(): Promise<void> {
    if (this.enabled) return this.startingPromise ?? Promise.resolve();
    this.enabled = true;
    this.muted = false;
    this.wasEnabled = true;
    this.notifyStateChange();
    this.socket.emit('voice:enable');
    this.startingPromise = this.startMediasoup()
      .catch((err) => {
        this.emitError('start_failed', err instanceof Error ? err.message : String(err));
        this.enabled = false;
        this.wasEnabled = false;
        this.muted = true;
        this.socket.emit('voice:disable');
        this.notifyStateChange();
      });
    return this.startingPromise;
  }

  async stop(): Promise<void> {
    if (!this.enabled) return;
    this.enabled = false;
    this.wasEnabled = false;
    this.notifyStateChange();
    this.socket.emit('voice:disable');
    await this.teardown();
  }

  private async startMediasoup(): Promise<void> {
    this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const audioTrack = this.localStream.getAudioTracks()[0];
    if (!audioTrack) throw new Error('no audio track in getUserMedia result');

    const capsAck = await this.requestRtpCapabilities();
    if (!capsAck.ok) throw new Error(`rtp-capabilities: ${capsAck.error}`);

    this.device = new Device();
    await this.device.load({ routerRtpCapabilities: capsAck.rtpCapabilities });

    const sendOpts = await this.requestTransportOptions('send');
    if (!sendOpts.ok) throw new Error(`create-transport(send): ${sendOpts.error}`);
    this.sendTransport = this.device.createSendTransport({
      id: sendOpts.transportId,
      iceParameters: sendOpts.iceParameters,
      iceCandidates: sendOpts.iceCandidates,
      dtlsParameters: sendOpts.dtlsParameters,
      iceServers: ICE_SERVERS,
    });
    this.sendTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this.socket.emit(
        'voice:connect-transport',
        { transportId: this.sendTransport!.id, direction: 'send', dtlsParameters },
      );
      callback();
    });
    this.sendTransport.on('produce', ({ kind, rtpParameters }, callback, errback) => {
      this.socket.emit(
        'voice:produce',
        { kind: 'audio' as const, rtpParameters: rtpParameters as unknown },
        (resp: unknown) => {
          const r = resp as { ok: true; producerId: string } | ErrAck;
          if (!r.ok) {
            errback(new Error(r.error));
            return;
          }
          callback({ id: r.producerId });
        },
      );
    });

    if (!this.sendTransport) throw new Error('send transport not created');
    this.producer = await this.sendTransport.produce({ track: audioTrack });
    if (this.muted) this.producer.pause();

    const recvOpts = await this.requestTransportOptions('recv');
    if (!recvOpts.ok) throw new Error(`create-transport(recv): ${recvOpts.error}`);
    this.recvTransport = this.device.createRecvTransport({
      id: recvOpts.transportId,
      iceParameters: recvOpts.iceParameters,
      iceCandidates: recvOpts.iceCandidates,
      dtlsParameters: recvOpts.dtlsParameters,
      iceServers: ICE_SERVERS,
    });
    this.recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this.socket.emit(
        'voice:connect-transport',
        { transportId: this.recvTransport!.id, direction: 'recv', dtlsParameters },
      );
      callback();
    });
  }

  private async consumeNewProducer(producerId: string, _playerId: string): Promise<void> {
    if (!this.recvTransport) return;
    if (this.consumers.has(producerId)) return;

    const capsAck = await this.requestRtpCapabilities();
    if (!capsAck.ok) {
      this.emitError('rtp_caps_failed', capsAck.error);
      return;
    }

    const ack = await this.requestConsume(producerId, capsAck.rtpCapabilities);
    if (!ack.ok) {
      this.emitError('consume_failed', ack.error);
      return;
    }

    const consumer = await this.recvTransport.consume({
      id: ack.consumerId,
      producerId: ack.producerId,
      kind: ack.kind,
      rtpParameters: ack.rtpParameters,
    });
    this.consumers.set(producerId, consumer);

    const stream = new MediaStream([consumer.track]);
    const audio = new Audio();
    audio.srcObject = stream;
    audio.autoplay = true;
    audio.muted = this.muted;
    document.body.appendChild(audio);
    this.remoteAudios.set(producerId, audio);

    this.socket.emit('voice:resume-consumer', { consumerId: consumer.id });
    void audio.play().catch((err) => {
      this.emitError('audio_play_failed', err instanceof Error ? err.message : String(err));
    });
  }

  private async teardown(): Promise<void> {
    if (this.producer) {
      try {
        this.producer.close();
      } catch {
        // ignore
      }
      this.producer = null;
    }
    for (const c of this.consumers.values()) {
      try {
        c.close();
      } catch {
        // ignore
      }
    }
    this.consumers.clear();
    if (this.sendTransport) {
      try {
        this.sendTransport.close();
      } catch {
        // ignore
      }
      this.sendTransport = null;
    }
    if (this.recvTransport) {
      try {
        this.recvTransport.close();
      } catch {
        // ignore
      }
      this.recvTransport = null;
    }
    for (const a of this.remoteAudios.values()) {
      a.srcObject = null;
      a.remove();
    }
    this.remoteAudios.clear();
    if (this.localStream) {
      for (const t of this.localStream.getTracks()) t.stop();
      this.localStream = null;
    }
    this.device = null;
  }

  private requestRtpCapabilities(): Promise<RtpCapsAck | ErrAck> {
    return new Promise((resolve) => {
      this.socket.emit('voice:request-rtp-capabilities', (resp: unknown) => {
        resolve(resp as RtpCapsAck | ErrAck);
      });
    });
  }

  private requestTransportOptions(direction: 'send' | 'recv'): Promise<TransportOpts | ErrAck> {
    return new Promise((resolve) => {
      this.socket.emit('voice:create-transport', { direction }, (resp: unknown) => {
        resolve(resp as TransportOpts | ErrAck);
      });
    });
  }

  private requestConsume(
    producerId: string,
    rtpCapabilities: MsTypes.RtpCapabilities,
  ): Promise<ConsumerOpts | ErrAck> {
    return new Promise((resolve) => {
      this.socket.emit(
        'voice:consume',
        { producerId, rtpCapabilities: rtpCapabilities as unknown },
        (resp: unknown) => {
          resolve(resp as ConsumerOpts | ErrAck);
        },
      );
    });
  }

  private bindSocketHandlers(): void {
    if (this.socketHandlersBound) return;
    this.socketHandlersBound = true;
    const sock = this.socket as unknown as {
      on: (e: string, h: (...a: unknown[]) => void) => unknown;
    };
    sock.on('connect', () => {
      if (this.wasEnabled) {
        void this.start();
      }
    });
    sock.on('disconnect', () => {
      // The server destroys our VoicePeer on socket disconnect, so the local
      // mediasoup pipeline is broken too. Reset `enabled` (so the next start()
      // actually rebuilds instead of short-circuiting on its idempotency check)
      // and tear down the transports. Preserve `wasEnabled` so reconnect
      // restores the user's intent.
      if (this.enabled) {
        this.enabled = false;
        void this.teardown();
      }
    });
    sock.on('voice:peer-enabled', ((info: { playerId: string }) => {
      for (const h of this.peerHandlers) h({ playerId: info.playerId, enabled: true });
    }) as never);
    sock.on('voice:peer-disabled', ((info: { playerId: string }) => {
      for (const h of this.peerHandlers) h({ playerId: info.playerId, enabled: false });
    }) as never);
    sock.on('voice:new-producer', ((info: { playerId: string; producerId: string }) => {
      void this.consumeNewProducer(info.producerId, info.playerId).catch((err) => {
        this.emitError('consume_setup_failed', err instanceof Error ? err.message : String(err));
      });
    }) as never);
    sock.on('voice:producer-closed', ((info: { producerId: string }) => {
      const consumer = this.consumers.get(info.producerId);
      if (consumer) {
        try {
          consumer.close();
        } catch {
          // ignore
        }
        this.consumers.delete(info.producerId);
      }
      const audio = this.remoteAudios.get(info.producerId);
      if (audio) {
        audio.srcObject = null;
        audio.remove();
        this.remoteAudios.delete(info.producerId);
      }
    }) as never);
    sock.on('voice:error', ((err: VoiceError) => {
      for (const h of this.errorHandlers) h(err);
    }) as never);
  }

  private unbindSocketHandlers(): void {
    if (!this.socketHandlersBound) return;
    this.socketHandlersBound = false;
    const sock = this.socket as unknown as {
      off: (e: string) => unknown;
    };
    sock.off('connect');
    sock.off('disconnect');
    sock.off('voice:peer-enabled');
    sock.off('voice:peer-disabled');
    sock.off('voice:new-producer');
    sock.off('voice:producer-closed');
    sock.off('voice:error');
  }

  private emitError(code: string, message: string): void {
    const err = { code, message };
    console.error(`[voice] ${code}: ${message}`);
    for (const h of this.errorHandlers) h(err);
  }

  private notifyStateChange(): void {
    for (const h of this.stateHandlers) h();
  }

  destroy(): void {
    this.unbindSocketHandlers();
    void this.teardown();
  }
}
